import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import UsersAdmin from "./pages/admin/UsersAdmin";
import CardsAdmin from "./pages/admin/CardsAdmin";
import CategoriesAdmin from "./pages/admin/CategoriesAdmin";
import DepartmentsAdmin from "./pages/admin/DepartmentsAdmin";
import CompaniesAdmin from "./pages/admin/CompaniesAdmin";
import PlaidIntegrationsAdmin from "./pages/admin/PlaidIntegrationsAdmin";
import ExpenseList from "./pages/expenses/ExpenseList";
import ExpenseForm from "./pages/expenses/ExpenseForm";
import ExpenseDetail from "./pages/expenses/ExpenseDetail";
import StatementsList from "./pages/statements/StatementsList";
import StatementDetail from "./pages/statements/StatementDetail";
import Inbox from "./pages/inbox/Inbox";
import { ReportsHome, ReportDetail, DigestPage, DriverChargebacksPage, SpendByCompanyPage } from "./pages/reports/Reports";
import Account from "./pages/Account";
import DriverCharges from "./pages/DriverCharges";
import LesseeCharges from "./pages/LesseeCharges";
import EquipmentRepairs from "./pages/EquipmentRepairs";
import ShopCredits from "./pages/ShopCredits";
import AdminLanding from "./pages/AdminLanding";
import PermissionsMatrix from "./pages/admin/PermissionsMatrix";

// ---------------------------------------------------------------------------
// Role gates — single source of truth for `<ProtectedRoute roles={...}>`.
// Change a role's reach in one place. Names match call-site intent; admin is
// implicitly included in ACCOUNTING_ONLY because admins always see everything.
// ---------------------------------------------------------------------------
const ADMIN_ONLY = ["admin"];
const ACCOUNTING_ONLY = ["admin", "accounting"];
const WORKER_ONLY = ["worker"];
const ALL_ROLES = ["admin", "accounting", "worker"];

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Landing />} />
            <Route path="/account" element={<Account />} />
            <Route path="/expenses" element={<ExpenseList />} />
            <Route path="/expenses/new" element={<ExpenseForm />} />
            <Route path="/expenses/:id" element={<ExpenseDetail />} />
            <Route path="/expenses/:id/edit" element={<ExpenseForm />} />
            <Route
              path="/statements"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <StatementsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statements/:id"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <StatementDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute roles={WORKER_ONLY}>
                  <Inbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver-charges"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <DriverCharges />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lessee-charges"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <LesseeCharges />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <ReportsHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/digest"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <DigestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/driver-chargebacks"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <DriverChargebacksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/spend-by-company"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <SpendByCompanyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:key"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <ReportDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment-repairs"
              element={
                <ProtectedRoute roles={ALL_ROLES}>
                  <EquipmentRepairs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-credits"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <ShopCredits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={ADMIN_ONLY}>
                  <AdminLanding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={ADMIN_ONLY}>
                  <UsersAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cards"
              element={
                <ProtectedRoute roles={ADMIN_ONLY}>
                  <CardsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedRoute roles={ADMIN_ONLY}>
                  <CategoriesAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/departments"
              element={
                <ProtectedRoute roles={ADMIN_ONLY}>
                  <DepartmentsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/companies"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <CompaniesAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/permissions"
              element={
                <ProtectedRoute roles={ADMIN_ONLY}>
                  <PermissionsMatrix />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/integrations/plaid"
              element={
                <ProtectedRoute roles={ACCOUNTING_ONLY}>
                  <PlaidIntegrationsAdmin />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
